import React, { useMemo } from 'react';
import { DialogTitle, DialogContent, IconButton, Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FulfillmentPlan } from '../data/FulfillmentPlanData';
import useMemoTranslation from '../hooks/UseMemoTranslation';
import { CompactGridWrapper } from '../components/grid/CompactGridWrapper';
import { PersistentDialog } from '../components/PersistentDialog';
import { useHistory } from 'react-router-dom';

interface FulfillmentPlanTransitionModalProps {
  open: boolean;
  onClose: () => void;
  stepTypeId: string;
  fulfillmentPlans: FulfillmentPlan[];
  fulfillmentPlanId: string;
  researchProjectId?: string;
}

interface ModalRow {
  id: string;
  researchProjectName: string;
  researchProjectId: string;
  stepName: string;
  workLeftCount: number;
  passedCount: number;
  failedCount: number;
  remainingCount: number;
  completedCount: number;
  totalCount: number;
}

const formatCount = (params: { value: number }) => (params.value === 0 ? '--' : params.value);

const COUNT_COLUMN_CONFIG = {
  width: 90,
  align: 'center' as const,
  headerAlign: 'center' as const,
  valueFormatter: formatCount,
};

export const FulfillmentPlanTransitionModal = (props: FulfillmentPlanTransitionModalProps) => {
  const { open, onClose, stepTypeId, fulfillmentPlans, fulfillmentPlanId, researchProjectId } = props;
  const { t } = useMemoTranslation();
  const history = useHistory();

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'researchProjectName',
        headerName: t('researchProject'),
        width: 200,
        flex: 1,
        renderCell: params => (
          <Box
            component='span'
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': { color: 'primary.dark' },
            }}
            onClick={() => {
              history.push(`/research-project/${params.row.researchProjectId}`);
              onClose();
            }}
          >
            {params.value}
          </Box>
        ),
      },
      {
        field: 'stepName',
        headerName: 'Step Name',
        width: 200,
      },
      {
        field: 'workLeftCount',
        headerName: t('needsCheckBy'),
        ...COUNT_COLUMN_CONFIG,
        width: 120,
      },
      {
        field: 'passedCount',
        headerName: t('passed'),
        ...COUNT_COLUMN_CONFIG,
      },
      {
        field: 'failedCount',
        headerName: t('failed'),
        ...COUNT_COLUMN_CONFIG,
      },
      {
        field: 'remainingCount',
        headerName: t('remaining'),
        ...COUNT_COLUMN_CONFIG,
      },
      {
        field: 'completedCount',
        headerName: t('exited'),
        ...COUNT_COLUMN_CONFIG,
      },
      {
        field: 'totalCount',
        headerName: t('total'),
        ...COUNT_COLUMN_CONFIG,
      },
    ],
    [t, history, onClose]
  );

  const rows: ModalRow[] = useMemo(() => {
    const plan = fulfillmentPlans.find(p => p.fulfillmentPlanId === fulfillmentPlanId);

    if (!plan?.researchProjects) return [];

    // Filter to specific research project if provided
    const projects = researchProjectId
      ? plan.researchProjects.filter(project => project.researchProjectId === researchProjectId)
      : plan.researchProjects;

    return projects.flatMap(project => {
      const stepType = project.stepTypes?.[stepTypeId];
      if (!stepType) return [];

      return stepType.steps.map((step, index) => ({
        id: `${project.researchProjectId}_${step.name}_${index}`,
        researchProjectName: project.name,
        researchProjectId: project.researchProjectId,
        stepName: step.name,
        workLeftCount: step.workLeftCount,
        passedCount: step.passedCount,
        failedCount: step.failedCount,
        remainingCount: step.remainingCount,
        completedCount: step.completedCount,
        totalCount: step.totalCount,
      }));
    });
  }, [fulfillmentPlans, fulfillmentPlanId, stepTypeId, researchProjectId]);

  const { planName, stepTypeDisplayName, researchProjectName } = useMemo(() => {
    const plan = fulfillmentPlans?.find(p => p.fulfillmentPlanId === fulfillmentPlanId);
    const stepType = plan?.stepTypes?.[stepTypeId];
    const researchProject = researchProjectId
      ? plan?.researchProjects?.find(p => p.researchProjectId === researchProjectId)
      : null;

    const displayName = stepType?.displayName || '';
    const finalDisplayName = displayName === 'GenericWithSampleOutput' ? 'Assign ID' : displayName;

    return {
      planName: plan?.name || '',
      stepTypeDisplayName: finalDisplayName,
      researchProjectName: researchProject?.name || '',
    };
  }, [fulfillmentPlans, fulfillmentPlanId, stepTypeId, researchProjectId]);

  return (
    <PersistentDialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h5'>
            {planName} - {stepTypeDisplayName}
            {researchProjectName && `: ${researchProjectName}`}
          </Typography>
          <IconButton edge='end' color='inherit' onClick={onClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ height: 400 }}>
          <CompactGridWrapper
            rows={rows}
            columns={columns}
            getRowId={row => row.id}
            disableRowSelectionOnClick
            disableColumnFilter={true}
            disableColumnMenu={true}
            hideFooterSelectedRowCount={true}
          />
        </Box>
      </DialogContent>
    </PersistentDialog>
  );
};
